import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { apiUrl } from '../config/api';
import { BookOpen } from 'lucide-react';

/**
 * Handles the /add/:bookId route triggered by share links and QR codes.
 *
 * Flow:
 *   - Authenticated → POST /api/library/add, navigate to /my-library
 *   - Not authenticated → save bookId to localStorage as pendingLibraryAdd,
 *     navigate to /login. Login.jsx checks pendingLibraryAdd after successful
 *     login and routes back here so the add can complete.
 *
 * Pending intent expires after 24 hours so stale links don't surprise users.
 */

const PENDING_KEY = 'readwell_pendingLibraryAdd';
const PENDING_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

export default function AddToLibrary() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading, token } = useAuth();
  const { toast } = useToast();
  // Guard so we only run the add flow once (StrictMode double-mounts in dev)
  const ranRef = useRef(false);

  useEffect(() => {
    // Wait for auth state to finish loading
    if (loading) return;
    if (ranRef.current) return;
    ranRef.current = true;

    // Validate bookId is a number — defends against /add/foobar
    const numericId = parseInt(bookId, 10);
    if (!bookId || Number.isNaN(numericId)) {
      toast({
        variant: 'destructive',
        title: 'Invalid link',
        description: 'That share link is malformed.',
      });
      navigate('/home', { replace: true });
      return;
    }

    if (!isAuthenticated) {
      // Save the intent and bounce to login
      try {
        localStorage.setItem(
          PENDING_KEY,
          JSON.stringify({ bookId: numericId, addedAt: Date.now() })
        );
      } catch (err) {
        // localStorage can fail in private mode — degrade gracefully
        console.warn('[AddToLibrary] localStorage unavailable:', err);
      }
      navigate('/login', { replace: true });
      return;
    }

    // Authenticated — add the book to library
    const addToLibrary = async () => {
      try {
        const response = await fetch(apiUrl('/api/library/add'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ book_id: numericId, source: 'saved' }),
        });

        if (response.ok) {
          toast({
            title: 'Added to your library',
            description: 'Find it under My Library.',
          });
          navigate('/my-library', { replace: true });
        } else if (response.status === 404) {
          toast({
            variant: 'destructive',
            title: 'Book not found',
            description: "That book doesn't exist (it may have been removed).",
          });
          navigate('/home', { replace: true });
        } else if (response.status === 409) {
          // Already in library — backend may signal this; treat as success
          toast({
            title: 'Already in your library',
            description: 'This book is already saved.',
          });
          navigate('/my-library', { replace: true });
        } else {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to add book');
        }
      } catch (err) {
        console.error('[AddToLibrary] Error:', err);
        toast({
          variant: 'destructive',
          title: 'Something went wrong',
          description: err.message || "We couldn't add the book. Try again later.",
        });
        navigate('/home', { replace: true });
      }
    };

    addToLibrary();
  }, [bookId, isAuthenticated, loading, navigate, toast, token]);

  // Loading screen — shown briefly while auth resolves and the add API call runs
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <BookOpen className="h-12 w-12 text-library-500 mx-auto mb-4 animate-pulse" />
        <p className="text-lg font-medium text-slate-900">Adding to your library…</p>
        <p className="text-sm text-muted-foreground mt-1">One moment.</p>
      </div>
    </div>
  );
}

/**
 * Helper used by Login.jsx to consume a pending library-add intent.
 * Returns the bookId if a valid (unexpired) pending intent exists, else null.
 * Always clears the localStorage entry as a side effect.
 */
export function consumePendingLibraryAdd() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.bookId !== 'number') return null;
    if (Date.now() - parsed.addedAt > PENDING_EXPIRY_MS) return null;
    return parsed.bookId;
  } catch {
    return null;
  }
}
